/**
 * <bundle-image-gallery> — Category-based layered image display for the
 * bundle builder. Listens for product selection/deselection events and
 * toggles image visibility via data-visible attributes.
 *
 * Deferred image loading: non-preset product images are rendered inside
 * <template data-deferred-image> tags so the browser skips them during
 * initial page load. Once all eager images have finished loading,
 * deferred templates are hydrated so the remaining images begin
 * fetching. If a user selects a product whose image hasn't been
 * hydrated yet, it's activated immediately on demand.
 */
class BundleImageGallery extends HTMLElement {
  #controller;
  #selections = new Map();

  connectedCallback() {
    this.#controller = new AbortController();
    const opts = { signal: this.#controller.signal };

    document.addEventListener('bundle:product-selected', (e) => {
      this.#handleSelection(e);
    }, opts);

    document.addEventListener('bundle:product-deselected', (e) => {
      const { category } = e.detail;
      if (!category) return;
      this.#selections.delete(category);
      this.#updateCategoryImages(category);
    }, opts);

    this.#hydrateWhenReady();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * Wait for every eager <img> to finish loading, signal readiness on
   * the section, then activate all deferred <template> images.
   */
  async #hydrateWhenReady() {
    const pending = [];
    for (const img of this.querySelectorAll('img.bundle-image-gallery__image')) {
      if (!img.complete) {
        pending.push(new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        }));
      }
    }

    await Promise.allSettled(pending);
    this.closest('.bundle-builder')?.setAttribute('data-gallery-ready', '');

    for (const tpl of this.querySelectorAll('template[data-deferred-image]')) {
      this.#hydrateTemplate(tpl);
    }
  }

  #hydrateTemplate(template) {
    const parent = template.parentElement;
    if (!parent) return;
    parent.appendChild(template.content.cloneNode(true));
    template.remove();
  }

  #handleSelection(event) {
    const { productHandle, variantId, category, isOptOut } = event.detail;
    if (!category) return;

    this.#selections.set(category, {
      productHandle: isOptOut ? null : productHandle,
      variantId: isOptOut ? null : variantId,
      isOptOut,
    });

    this.#updateCategoryImages(category);
  }

  #updateCategoryImages(category) {
    const wrappers = this.querySelectorAll(
      `.bundle-image-gallery__product-image[data-category="${category}"]`
    );
    if (wrappers.length === 0) return;

    const selection = this.#selections.get(category);

    for (const wrapper of wrappers) {
      wrapper.dataset.visible = 'false';
      wrapper.setAttribute('aria-hidden', 'true');
    }

    if (!selection || selection.isOptOut || !selection.productHandle) return;

    let match;

    if (selection.variantId) {
      match = this.querySelector(
        `.bundle-image-gallery__product-image[data-category="${category}"][data-variant-id="${selection.variantId}"]`
      );
    }

    if (!match) {
      match = this.querySelector(
        `.bundle-image-gallery__product-image[data-category="${category}"][data-product-handle="${selection.productHandle}"]`
      );
    }

    if (!match) return;

    const tpl = match.querySelector('template[data-deferred-image]');
    if (tpl) this.#hydrateTemplate(tpl);
    match.dataset.visible = 'true';
    match.removeAttribute('aria-hidden');
  }
}

if (!customElements.get('bundle-image-gallery')) {
  customElements.define('bundle-image-gallery', BundleImageGallery);
}
